import * as z from "zod";
import { ENTITY_NAMES, ENTITY_SCHEMAS, type EntityName } from "./schema";

/*
 * Sync protocol (Evernote USN model).
 *
 * Pull: GET /api/sync/pull?since=<cursor>&limit=<n>
 *   → { cursor, more, changes: [{ t, row }] } where every row has seq <= cursor.
 *   Clients store `cursor` and pull again while `more` is true.
 *
 * Push: POST /api/sync/push { mutations }
 *   A mutation sets some fields of one row (creating it if missing) or deletes it.
 *   Notes carry `base` = the version the edit started from. If the server's
 *   version moved on and title/body differ, the server keeps its copy and
 *   stores the pushed content as a new "conflicting copy" note.
 *   Other fields and other entities are last-writer-wins per field.
 */

export const PULL_LIMIT_MAX = 250;
/** Notes carry their bodies, so a pull page holds at most this many (the rest of the page is other rows). */
export const PULL_NOTES_MAX = 100;
/**
 * Workers Free allows ~50 D1 queries per request. A push costs 1 counter bump,
 * up to 2 statements per mutation (write + history snapshot or conflict copy),
 * plus the auth lookup and state reads, so 20 keeps us safely under.
 */
export const PUSH_MAX = 20;

/** Push error for a row over D1's 2 MB limit (a giant note or scratch pad). */
export const TOO_LARGE = "too large to sync";

export const mutationSchema = z.object({
  mid: z.number().int(), // client outbox id, echoed back
  t: z.enum(ENTITY_NAMES as [EntityName, ...EntityName[]]),
  id: z.string().min(1).max(64),
  set: z.record(z.string(), z.unknown()).optional(),
  del: z.literal(true).optional(),
  base: z.number().int().optional(),
});
export type Mutation = z.infer<typeof mutationSchema>;

export const pushRequestSchema = z.object({
  mutations: z.array(mutationSchema).max(PUSH_MAX),
});
export type PushRequest = z.infer<typeof pushRequestSchema>;

export type MutationResult =
  | { mid: number; ok: true; seq: number; version?: number }
  | { mid: number; ok: true; seq: number; version: number; conflictCopyId: string }
  | { mid: number; ok: false; error: string };

export interface PushResponse {
  results: MutationResult[];
  cursor: number; // server seq after this push
}

export interface Change {
  t: EntityName;
  row: Record<string, unknown> & { id: string; seq: number; deleted: 0 | 1 };
}

export interface PullResponse {
  cursor: number;
  more: boolean;
  changes: Change[];
}

/** Validates the `set` part of a mutation against the entity schema (partial). */
export function validateSet(t: EntityName, set: Record<string, unknown>): Record<string, unknown> {
  return setSchema(t).parse(set) as Record<string, unknown>;
}
// Built once per entity: building it took ~0.35 ms, a third of a 20-mutation push's CPU (chat 10).
const setSchemas = new Map<EntityName, z.ZodType>();
function setSchema(t: EntityName): z.ZodType {
  let s = setSchemas.get(t);
  if (!s) setSchemas.set(t, (s = (ENTITY_SCHEMAS[t] as z.ZodObject<z.ZodRawShape>).omit({ id: true }).partial().strict()));
  return s;
}
